import { useState, useRef, useEffect } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { IconSparkles, IconSignature, IconMinus, IconPlus, IconZoomReset } from '@tabler/icons-react';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

const SIGNATURE_PAD_URL = 'https://sohozkaj.com/tools/image/signature-pad';

const BASE_H = 460;   // displayed image height at zoom = 1
const MIN_ZOOM = 1;
const MAX_ZOOM = 3;

// Convert a react-image-crop percent crop into displayed pixels, so the output
// is always computed from the current image layout (zoom-independent).
function percentToPixel(image, crop) {
    if (!image || !crop) return null;
    if (crop.unit === '%') {
        return {
            x: (crop.x / 100) * image.width,
            y: (crop.y / 100) * image.height,
            width: (crop.width / 100) * image.width,
            height: (crop.height / 100) * image.height,
        };
    }
    return crop;
}

// Crop the displayed image to the selected pixel area → WebP blob.
// `crop` is a PixelCrop relative to the *displayed* image; we scale it up to the
// image's natural resolution so the output keeps full quality.
// opts.outW/outH → force an exact output size (used by the size presets).
// opts.maxDim   → cap the longest side (keeps a hand-off small).
async function getCroppedBlob(image, crop, opts = {}) {
    if (!image || !crop?.width || !crop?.height) return null;
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    const w = Math.round(crop.width * scaleX);
    const h = Math.round(crop.height * scaleY);
    const sx = crop.x * scaleX;
    const sy = crop.y * scaleY;
    let outW = w;
    let outH = h;
    if (opts.outW && opts.outH) {
        outW = opts.outW;
        outH = opts.outH;
    } else if (opts.maxDim && Math.max(w, h) > opts.maxDim) {
        const s = opts.maxDim / Math.max(w, h);
        outW = Math.round(w * s);
        outH = Math.round(h * s);
    }
    const canvas = document.createElement('canvas');
    canvas.width = outW;
    canvas.height = outH;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, sx, sy, w, h, 0, 0, outW, outH);
    return new Promise((resolve) => canvas.toBlob((b) => resolve(b), 'image/webp', 0.9));
}

function blobToDataUrl(blob) {
    return new Promise((resolve, reject) => {
        const r = new FileReader();
        r.onload = () => resolve(r.result);
        r.onerror = reject;
        r.readAsDataURL(blob);
    });
}

// Key the GenerateImage page reads to preload a handed-off image.
const GENIMAGE_PRELOAD_KEY = 'genimage_preload';

/**
 * Crop a picked photo/signature before upload. The crop box is freely resizable
 * via its corner/edge handles, with zoom and preset sizes (300×300, 300×180 for
 * signatures). Photo gets a "make professional" link to /generate-image;
 * signature gets a link to the Signature Pad tool.
 */
export default function ImageCropModal({ open, file, kind, onClose, onCropped }) {
    const { t } = useTranslation();

    const [src, setSrc] = useState('');
    const [crop, setCrop] = useState();              // percent crop (kept responsive)
    const [aspect, setAspect] = useState(undefined); // active fixed aspect (or free)
    const [activePreset, setActivePreset] = useState('free');
    const [zoom, setZoom] = useState(1);
    const [saving, setSaving] = useState(false);
    const imgRef = useRef(null);

    // Preset crop sizes. Free = no forced aspect/output; the others force both a
    // fixed aspect and an exact pixel output size.
    const PRESETS = [
        { key: 'free', label: t('sohozJob.crop.free'), aspect: undefined },
        { key: 's300', label: '300×300', aspect: 1, outW: 300, outH: 300 },
        { key: 's300x180', label: '300×180', aspect: 300 / 180, outW: 300, outH: 180 },
    ];

    useEffect(() => {
        if (!file) return undefined;
        const url = URL.createObjectURL(file);
        setSrc(url);
        setCrop(undefined);
        setAspect(undefined);
        setActivePreset('free');
        setZoom(1);
        return () => URL.revokeObjectURL(url);
    }, [file]);

    // Seed an initial centered selection once the image dimensions are known.
    const onImageLoad = (e) => {
        const { width, height } = e.currentTarget;
        if (kind === 'signature') {
            // Signatures default to the 300×180 preset.
            setActivePreset('s300x180');
            setAspect(300 / 180);
            setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 300 / 180, width, height), width, height));
        } else {
            setActivePreset('free');
            setAspect(undefined);
            setCrop(centerCrop({ unit: '%', width: 60, height: 80 }, width, height));
        }
    };

    const applyPreset = (preset) => {
        setActivePreset(preset.key);
        const img = imgRef.current;
        if (!img) return;
        if (!preset.aspect) {
            setAspect(undefined);
            setCrop(centerCrop({ unit: '%', width: 80, height: 80 }, img.width, img.height));
            return;
        }
        setAspect(preset.aspect);
        setCrop(centerCrop(makeAspectCrop({ unit: '%', width: 80 }, preset.aspect, img.width, img.height), img.width, img.height));
    };

    const currentPreset = () => PRESETS.find((p) => p.key === activePreset);

    const handleUse = async () => {
        const pixel = percentToPixel(imgRef.current, crop);
        if (!pixel) return;
        setSaving(true);
        try {
            const p = currentPreset();
            const opts = p?.outW ? { outW: p.outW, outH: p.outH } : {};
            const blob = await getCroppedBlob(imgRef.current, pixel, opts);
            if (blob) onCropped(blob);
        } finally {
            setSaving(false);
        }
    };

    // Hand off the (cropped) image to the Generate Image editor — no re-upload.
    const handleMakePro = async () => {
        try {
            const pixel = percentToPixel(imgRef.current, crop);
            const blob = pixel ? await getCroppedBlob(imgRef.current, pixel, { maxDim: 1400 }) : null;
            const dataUrl = blob ? await blobToDataUrl(blob) : src;
            localStorage.setItem(GENIMAGE_PRELOAD_KEY, dataUrl);
        } catch { /* if storage fails, the page just opens empty */ }
        window.open(`${window.location.origin}/generate-image`, '_blank', 'noopener');
    };

    // Use the original image without cropping.
    const handleSkip = () => {
        if (file) onCropped(file);
    };

    const stepZoom = (delta) => setZoom((z) => Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, +(z + delta).toFixed(2))));

    return (
        <Dialog open={open} onOpenChange={(v) => { if (!v && !saving) onClose(); }}>
            <DialogContent className="max-w-2xl font-hind max-h-[92vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle>{kind === 'signature' ? t('sohozJob.crop.titleSignature') : t('sohozJob.crop.titlePhoto')}</DialogTitle>
                </DialogHeader>

                {/* ── Preset sizes + zoom controls ── */}
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('sohozJob.crop.preset')}</span>
                    {PRESETS.map((p) => (
                        <button
                            key={p.key}
                            type="button"
                            onClick={() => applyPreset(p)}
                            className={`px-3 py-1.5 text-xs font-semibold rounded-full border transition-colors ${
                                activePreset === p.key
                                    ? 'bg-primary text-white border-primary'
                                    : 'bg-white dark:bg-zinc-800 text-zinc-600 dark:text-zinc-300 border-zinc-200 dark:border-zinc-700 hover:border-primary/50'
                            }`}
                        >
                            {p.label}
                        </button>
                    ))}

                    <div className="flex-1 flex items-center justify-end gap-1.5">
                        <button type="button" onClick={() => stepZoom(-0.2)} disabled={zoom <= MIN_ZOOM} className="h-8 w-8 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-40" aria-label={t('sohozJob.crop.zoom')}>
                            <IconMinus size={16} />
                        </button>
                        <input
                            type="range"
                            min={MIN_ZOOM}
                            max={MAX_ZOOM}
                            step={0.1}
                            value={zoom}
                            onChange={(e) => setZoom(Number(e.target.value))}
                            className="w-24 accent-primary cursor-pointer"
                            aria-label={t('sohozJob.crop.zoom')}
                        />
                        <button type="button" onClick={() => stepZoom(0.2)} disabled={zoom >= MAX_ZOOM} className="h-8 w-8 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-40" aria-label={t('sohozJob.crop.zoom')}>
                            <IconPlus size={16} />
                        </button>
                        <button type="button" onClick={() => setZoom(1)} disabled={zoom === 1} className="h-8 w-8 flex items-center justify-center rounded-lg border border-zinc-200 dark:border-zinc-700 text-zinc-600 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-40" aria-label={t('sohozJob.crop.zoom')}>
                            <IconZoomReset size={16} />
                        </button>
                    </div>
                </div>

                <div className="relative my-3 w-full bg-zinc-100 dark:bg-zinc-800 rounded-xl overflow-auto flex items-center justify-center py-4" style={{ height: BASE_H }}>
                    {src && (
                        <ReactCrop
                            crop={crop}
                            onChange={(_px, percentCrop) => setCrop(percentCrop)}
                            aspect={aspect}
                            keepSelection
                            minWidth={24}
                            minHeight={24}
                        >
                            <img
                                ref={imgRef}
                                src={src}
                                alt=""
                                onLoad={onImageLoad}
                                style={{ maxHeight: (BASE_H - 32) * zoom, maxWidth: 'none', width: 'auto', display: 'block' }}
                            />
                        </ReactCrop>
                    )}
                </div>

                {/* Drag hint + tool action on one row */}
                <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-500 dark:text-zinc-400 flex-1 min-w-0">{t('sohozJob.crop.dragHint')}</span>
                    {kind === 'photo' ? (
                        <button
                            type="button"
                            onClick={handleMakePro}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold rounded-lg border border-primary/40 text-primary hover:bg-primary/5 transition-colors whitespace-nowrap flex-shrink-0"
                        >
                            <IconSparkles size={16} /> {t('sohozJob.crop.makePro')}
                        </button>
                    ) : (
                        <button
                            type="button"
                            onClick={() => window.open(SIGNATURE_PAD_URL, '_blank', 'noopener')}
                            className="inline-flex items-center gap-1.5 px-3.5 py-2 text-[13px] font-semibold rounded-lg border border-primary/40 text-primary hover:bg-primary/5 transition-colors whitespace-nowrap flex-shrink-0"
                        >
                            <IconSignature size={16} /> {t('sohozJob.crop.createSignature')}
                        </button>
                    )}
                </div>

                <DialogFooter className="mt-2 pt-4 border-t border-zinc-200 dark:border-zinc-700">
                    <button type="button" onClick={onClose} disabled={saving} className="px-4 py-2 text-sm text-zinc-600 dark:text-zinc-400 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-lg transition-colors disabled:opacity-60">
                        {t('common.cancel')}
                    </button>
                    <button type="button" onClick={handleSkip} disabled={saving} className="px-4 py-2 text-sm font-medium text-zinc-600 dark:text-zinc-300 border border-zinc-200 dark:border-zinc-700 rounded-lg hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors disabled:opacity-60">
                        {t('sohozJob.crop.skip')}
                    </button>
                    <button type="button" onClick={handleUse} disabled={saving || !crop} className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold bg-primary hover:bg-primary/90 text-white rounded-lg transition-colors disabled:opacity-60">
                        {saving
                            ? <><span className="w-4 h-4 border-2 border-white/40 border-t-white rounded-full animate-spin" /> {t('sohozJob.crop.processing')}</>
                            : t('sohozJob.crop.use')}
                    </button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
